/* eslint-disable max-depth */
import { useEffect, useMemo, useState } from 'react'
import { queryFfromList, querySourcesList } from '@/services/helpDocuments'
import { message as $message } from 'antd'
import memoize from 'lodash/memoize'
import { createWindow, getTerminaDomain } from '@/utils/utils'
import { manageAgentLogin } from '@/services/supplier'
import { OUTER_REGISTER_PREFIX, ERP_LOGIN_PREFIX } from './constants'

type companyTypeString = 'SUPPLIER' | 'CHANNEL'
interface useGetDataSourceType {
    currentCompanyType: companyTypeString
}
type SourceListType = SourceItemType[]

interface SourceItemType {
    source: string
    currentCompanyType: companyTypeString
}
type FfromListType = FfromItemType[]

interface FfromItemType {
    label: string
    value: string
}

export const useGetDataSource = (props: useGetDataSourceType) => {
    const { currentCompanyType } = props
    const [source, setSource] = useState<SourceListType>([])
    const [ffromList, setFfromList] = useState<FfromListType>([])

    useEffect(() => {
        const getFfromList = async () => {
            const res = await queryFfromList()

            if (res.success) {
                setFfromList(res.data)
            } else {
                $message.error(res.message)
            }
        }
        getFfromList()
    }, [])

    useEffect(() => {
        const getSourceData = async () => {
            const res = await querySourcesList({
                companyType: currentCompanyType
            })
            if (res.success) {
                setSource(res.data)
            } else {
                $message.error(res.message)
            }
        }
        getSourceData()
    }, [])
    const sourceData = useMemo(() => {
        if (source?.length > 0 && ffromList?.length > 0) {
            return source
                .filter(item => item.currentCompanyType === currentCompanyType)
                .map(item => ({
                    label: transSourceToLabel(item.source, ffromList),
                    value: item.source
                }))
        }
        return []
    }, [currentCompanyType, ffromList, source])

    return { sourceData }
}

const prefixMap = {
    [OUTER_REGISTER_PREFIX]: '主动注册-',
    [ERP_LOGIN_PREFIX]: 'ERP联登-'
}
const prefixList = Object.keys(prefixMap)

export const transSourceToLabel = memoize((source: string, ffromList: FfromListType): string => {
    let clearSource = source
    let prefix = ''

    for (let i = 0; i < prefixList.length; i++) {
        if (source.startsWith(prefixList[i])) {
            clearSource = source.replace(prefixList[i], '')
            prefix = prefixMap[prefixList[i]]
        }
    }

    const ffromListMap = {}
    for (let i = 0; i < ffromList.length; i++) {
        const { label, value } = ffromList[i]
        ffromListMap[value] = label
    }
    let res = ffromListMap[clearSource] || clearSource

    return prefix + res
})

const patchData = (data: any) => {
    if (data && Object.prototype.toString.call(data.token) === '[object Object]') {
        const { token, coId, uid } = data || {}
        const { access_token } = token || {}

        return {
            access_token,
            coId,
            uid
        }
    }

    return data || {}
}

export interface AgentLoginParams {
    /** 公司 ID */
    coId: string

    /** 代理登陆后跳转的页面 */
    redirect: string

    /** 商家签入状态 */
    checkInStatus?: string

    /** 跳转成功的回调 */
    onSuccess?: (_: { coId: string; uid: string }) => void
}

export interface AgentLoginByUserParams extends Omit<AgentLoginParams, 'coId'> {
    userData: {
        loginName: string
        coId: string
        uid: string
        userName: string
        roleIds: string
    }
}

const agentLoginByUser = async (params: Omit<AgentLoginByUserParams, 'checkInStatus'>) => {
    const { userData, onSuccess, redirect } = params
    const { loginName, coId: coId1, uid, coName, userName, roleIds } = userData || {}
    const response = await manageAgentLogin({ loginName, coId: coId1, uid, coName, userName, roleIds })
    if (response) {
        const { success, message, data } = response
        if (success) {
            const { access_token, coId: coId2, uid: uid1 } = patchData(data)
            if (access_token) {
                onSuccess?.({
                    coId: coId2,
                    uid: uid1
                })
                const win = createWindow()
                win.open(
                    `${getTerminaDomain(
                        window.PROJECT_CONFIG.accountTerminalName
                    )}/jumpPage/index?access_token=${access_token}&is_agent=1&redirect=${encodeURIComponent(redirect)}`
                )
                return true
            } else {
                $message.error('代理登录失败')
                return false
            }
        } else {
            $message.error(message)
            return false
        }
    }
    return false
}

// 代理登录子账号
export const agentLoginSubAccount = async (params: AgentLoginByUserParams) => {
    const { redirect, userData, onSuccess } = params

    if (userData) {
        await agentLoginByUser({ userData, onSuccess, redirect })
    }
}
